import { BillingStrategy, type AddOnsVO, type PlanGroup, type Rating, type Suspend } from '@ctct/domain-layer';
import type { AccountModificationFlowId } from '@ctct/infrastructure-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';

import { orderSummaryEventBus } from './OrderSummaryEventBus';
import { CountryBasedInfoService } from './services/CountryBasedInfoService';
import { CustomerOrderItemsService } from './services/CustomerOrderItemsService';
import { OrderSummaryVisibilityService } from './services/OrderSummaryVisibilityService';
import { RatingCallService } from './services/RatingCallService';
import { RatingPromoCodesService } from './services/RatingPromoCodesService';
import { TrialerOrderItemsService } from './services/TrialerOrderItemsService';
import type { ExternalStateValues, Init, OrderItemsService, OrderSummaryPageStore } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType, TranslationGetter } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type OrderSummaryData = MergedDataReturnType<[OrderItemsService]>;

export type OrderSummaryHandlers = MergedHandlersReturnType<[OrderItemsService]>;

type TApi = ReturnType<OrderItemsService['api']> &
  ReturnType<RatingCallService['api']> &
  ReturnType<OrderSummaryVisibilityService['api']> &
  ReturnType<CountryBasedInfoService['api']> &
  ReturnType<RatingPromoCodesService['api']>;

const performanceTracker = observabilityAppService.createPerformanceTracker('orderSummaryInit', {
  tags: ['application-layer', 'OrderSummaryManager'],
});

export class OrderSummaryManager extends WithApi<TApi> {
  private orderSummaryPageStore: OrderSummaryPageStore | null = null;

  /**
   * Returns promo codes currently in the rating payload (source of truth for redirects).
   */
  public getNewPromotionCodeNamesFromPayload(): string[] {
    return this.orderSummaryPageStore?.getState().ratingPayload?.newPromotionCodes ?? [];
  }

  public async getNewPromotionCodeNamesWhenReady(): Promise<string[]> {
    await this.getApiWhenReady();

    return this.getNewPromotionCodeNamesFromPayload();
  }

  /**
   * Synchronously clears applied promo codes from the rating payload.
   * Called before navigation on plan change so stale codes are not sent to rating.
   */
  public clearAllPromoCodesForPlanChange() {
    const { ratingPayload } = this.orderSummaryPageStore?.getState() ?? {};

    if (!ratingPayload?.newPromotionCodes.length) {
      return;
    }

    this.orderSummaryPageStore?.setState({
      ratingPayload: ratingPayload.withNewPromotionCodes([]),
    });
  }

  public async clearAllPromoCodesForPlanChangeWhenReady(): Promise<void> {
    await this.getApiWhenReady();

    this.clearAllPromoCodesForPlanChange();
  }

  public orderSummaryInit = ({
    pageStore,
    queries,
    t,
    flags,
    externalStateValues,
    billingStrategy,
    flowId,
    isSingleBillChildAccount,
    isSingleBillChildAccountPending = false,
    hideContactsTierLabel = false,
  }: Init) => {
    const [hideEditAddOns, isSmsBundle = false, isMobile = false] = flags;
    const [
      addOnsQuery,
      ratingQuery,
      billingAccountQuery,
      planGroupQuery,
      billingInfo,
      paymentPlanQuery,
      SMSEntitlementQuery,
      suspensionInfoQuery,
    ] = queries;

    this.orderSummaryPageStore = pageStore;

    const { data: rating, isFetching, error, isError } = ratingQuery;
    const {
      data: SMSEntitlement,
      isFetching: isFetchingSMSEntitlement,
      isError: isErrorSMSEntitlement,
    } = SMSEntitlementQuery;

    const { data: planGroup, isFetching: isFetchingPlanDetail, isError: isErrorPlanDetail } = planGroupQuery;

    orderSummaryEventBus.emit('ratingCallPending', isFetching, { source: 'asyncResponse' });

    const paymentPlanId = paymentPlanQuery.data?.getPaymentPlanGroupId();
    const isNonProfit = paymentPlanQuery.data?.getIsNonProfit();

    const ratingCallService = new RatingCallService(
      pageStore,
      billingStrategy === BillingStrategy.Stripe,
      addOnsQuery.data,
      rating,
      SMSEntitlement,
      billingAccountQuery.data
    );

    const orderItems = this.createOrderItemsService({
      pageStore,
      t,
      rating,
      planGroup,
      hideEditAddOns,
      isSmsBundle,
      isMobile,
      addOnsData: addOnsQuery.data,
      isNonProfit,
      flowId,
      suspend: suspensionInfoQuery?.data,
      billingStrategy,
      hideContactsTierLabel,
    });
    const orderSummaryVisibilityService = new OrderSummaryVisibilityService(pageStore);

    this.handleExternalStateValues(externalStateValues, ratingCallService, isSingleBillChildAccount);

    if (typeof paymentPlanId === 'number') {
      ratingCallService.updatePaymentPlanId(paymentPlanId);
    }

    const countryBasedInfoService = new CountryBasedInfoService(pageStore);

    const ratingPromoCodesService = new RatingPromoCodesService(pageStore);

    this.initApi({
      ...ratingCallService.api(),
      ...orderItems.api(),
      ...orderSummaryVisibilityService.api(),
      ...countryBasedInfoService.api(),
      ...ratingPromoCodesService.api(),
    });

    const isPending =
      isFetching ||
      billingInfo.isFetching ||
      isFetchingPlanDetail ||
      isFetchingSMSEntitlement ||
      isSingleBillChildAccountPending ||
      (billingStrategy === BillingStrategy.Stripe && externalStateValues.isPromoValidationPending);

    const hasError = isError || isErrorPlanDetail || isErrorSMSEntitlement;

    if (isPending) {
      performanceTracker.reportLoading();
    } else if (hasError) {
      performanceTracker.reportError({
        error: error ?? undefined,
        meta: {
          'ratingQuery.isError': !!isError,
          'planGroupQuery.isError': !!isErrorPlanDetail,
          'SMSEntitlementQuery.isError': !!isErrorSMSEntitlement,
        },
      });
    } else {
      performanceTracker.reportLoaded();
    }

    return {
      status: this.getStatus(isPending, hasError),
      data: {
        ...orderItems.data(),
        error,
      },
      handlers: {
        ...orderItems.handlers(),
        retry: () => {
          managerHelpers.retryQueries(queries);
        },
      },
      lifecycle: {
        onUnmountOrUnload() {
          performanceTracker.dispose();
        },
      } satisfies WidgetLifecycle,
    } as const;
  };

  private createOrderItemsService({
    pageStore,
    t,
    rating,
    planGroup,
    addOnsData,
    hideEditAddOns,
    isSmsBundle,
    isMobile,
    isNonProfit,
    suspend,
    flowId,
    billingStrategy,
    hideContactsTierLabel,
  }: {
    pageStore: OrderSummaryPageStore;
    t: TranslationGetter;
    rating: Rating | undefined;
    planGroup: PlanGroup | undefined;
    addOnsData: AddOnsVO | undefined;
    hideEditAddOns: boolean;
    isSmsBundle: boolean;
    isMobile: boolean;
    isNonProfit: boolean | undefined;
    suspend?: Suspend | null;
    flowId: AccountModificationFlowId;
    billingStrategy: BillingStrategy;
    hideContactsTierLabel: boolean;
  }): OrderItemsService {
    const isSocialStarter = planGroup?.isSocialStarter() ?? false;

    if (planGroup?.isCustomer()) {
      return new CustomerOrderItemsService({
        pageStore,
        t,
        rating,
        addOns: addOnsData,
        flowId,
        suspend,
        isStripeCustomer: billingStrategy === BillingStrategy.Stripe,
        isSocialStarter,
        isNonProfit,
      });
    }

    return new TrialerOrderItemsService({
      pageStore,
      t,
      rating,
      hideEditAddOns,
      isSmsBundle,
      isMobile,
      isNonProfit,
      isSocialStarter,
      isStripeCustomer: billingStrategy === BillingStrategy.Stripe,
      hideContactsTierLabel,
    });
  }

  private getStatus(isPending: boolean, isError: boolean) {
    if (isPending) return 'pending';

    if (isError) return 'error';

    return 'loaded';
  }

  private handleExternalStateValues = (
    externalStateValues: ExternalStateValues,
    ratingCall: RatingCallService,
    isSingleBillChildAccount: boolean
  ) => {
    const { selectedEmailPlanId, contactsTier, paymentPeriod, selectedAddOns } = externalStateValues;

    if (typeof selectedEmailPlanId === 'number') {
      ratingCall.updateEmailPlanId(selectedEmailPlanId);
    }

    if (typeof contactsTier === 'string') {
      ratingCall.updateContactsTier(contactsTier);
    }

    if (typeof paymentPeriod === 'string') {
      ratingCall.updatePaymentPeriod(paymentPeriod);
    }

    if (!isSingleBillChildAccount) {
      ratingCall.updateAddOns(selectedAddOns);
    }
  };
}
